import classNames from "classnames";
import { useState } from "react";
import Masonry from "react-responsive-masonry";
import { Badge } from "../../../../components/Badge/Badge.tsx";
import { Button } from "../../../../components/Button/Button.tsx";
import { BaseImage } from "../../../../components/Content/BaseImage.tsx";
import { ThumbnailContent } from "../../../../components/Content/ThumbnailContent.tsx";
import { EditableText } from "../../../../components/EditableText/EditableText.tsx";
import { PimentoStar } from "../../../../components/Icon/assets/PimentoStar.tsx";
import { Tooltip } from "../../../../components/Tooltip/Tooltip.tsx";
import { useAppMutation } from "../../../../http/useAppMutation.ts";
import { useAppQuery } from "../../../../http/useAppQuery.ts";
import { DEFAULT_STYLE_MODELS_PROPERTIES } from "../../../constants.ts";
import { useUserCanRenameModel } from "../../../Home/HomeIndex/hooks/useUserCanRenameModel.ts";
import { useWorkspaceUuid } from "../../../hooks/useWorkspaceUuid.ts";
import { ModelTrainDialog } from "../../../Model/ModelTrainDialog.tsx";
import type {
  ModelWithContents,
  ModelWithIsCompatibleAndTags,
} from "../../../types.ts";
import { IMAGE_RESOURCE_TYPE } from "../../constants.ts";
import { useRetrainModel } from "../../hooks/useRetrainModel.ts";
import { ModelDetails } from "../../ModelDetails/ModelDetails.tsx";

export const ModelDescription = ({
  model,
}: {
  model: ModelWithIsCompatibleAndTags;
}) => {
  const { data: modelWithContents } = useAppQuery<ModelWithContents>({
    queryKey: `references/${model.uuid}`,
  });

  const userCanRenameModel = useUserCanRenameModel(model);
  const workspaceUuid = useWorkspaceUuid();

  const { mutate: updateModel, isLoading: isUpdatingModel } = useAppMutation({
    path: "references/update",
    invalidate: [`workspaces/${workspaceUuid}/references`],
  }).mutation;

  const {
    isDisabled: isRetrainDisabled,
    isUserAllowed,
    modelRetrainStatus,
  } = useRetrainModel({ modelUuid: model.uuid });

  const [
    isTrainingModelPurchaseDialogOpen,
    setIsTrainingModelPurchaseDialogOpen,
  ] = useState(false);

  if (!modelWithContents) return null;

  const thumbnailUrl =
    modelWithContents.thumbnail_url ??
    DEFAULT_STYLE_MODELS_PROPERTIES[model.uuid]?.thumbnail_url;

  return (
    <div className="flex-col flex-fill h-full gap-300 pt-600 px-600 text-primary">
      <div className="flex-row gap-400">
        <div className="w-1600 h-1600 rounded-100 overflow-clip border-input-border-width">
          {thumbnailUrl ? (
            <ThumbnailContent
              resourceType={IMAGE_RESOURCE_TYPE}
              src={thumbnailUrl}
              className="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex-row-center bg-surface-emphasis-primary-rest">
              <PimentoStar size={32} className="fill-button-primary-rest" />
            </div>
          )}
        </div>
        <div className="flex-col flex-fill justify-center gap-150">
          <div className="flex-row">
            <EditableText
              value={model.name ?? "Untitled"}
              placeholder="Untitled"
              textClassName="heading-2xl truncate"
              onSave={(newValue) => {
                updateModel({
                  uuid: model.uuid,
                  name: newValue,
                });
              }}
              isLoading={isUpdatingModel}
              isEditable={userCanRenameModel}
            />
          </div>
          <div className="flex-row items-center w-fit gap-100">
            {!model.is_compatible && (
              <Tooltip
                side="bottom"
                align="start"
                content={
                  <div className="w-[200px] text-center ">
                    This model is not compatible with the current tool or model
                    selection, please choose another model.
                  </div>
                }
              >
                <span>
                  <Badge size="sm" color="error" label="Incompatible" />
                </span>
              </Tooltip>
            )}
            {(() => {
              switch (modelRetrainStatus) {
                case "training":
                  return (
                    <Badge
                      size="sm"
                      iconName="LoaderCircle"
                      color="cautionSecondary"
                      label="Model is training"
                    />
                  );
                case "outdated":
                  return (
                    <>
                      <Badge
                        size="sm"
                        color="cautionSecondary"
                        label="Needs retrain"
                        iconName="TriangleAlert"
                      />
                      {isUserAllowed && (
                        <>
                          <Button
                            onClick={() =>
                              setIsTrainingModelPurchaseDialogOpen(
                                !isTrainingModelPurchaseDialogOpen,
                              )
                            }
                            disabled={isRetrainDisabled}
                            size="sm"
                            variant="secondary"
                          >
                            Retrain model
                          </Button>
                          <ModelTrainDialog
                            isOpen={isTrainingModelPurchaseDialogOpen}
                            onOpenChange={(open) => {
                              setIsTrainingModelPurchaseDialogOpen(open);
                            }}
                            modelUuid={model.uuid}
                            trainAction="retrain"
                          />
                        </>
                      )}
                    </>
                  );
                case "upToDate":
                case "unknown":
                  return null;
              }
            })()}
          </div>
        </div>
      </div>
      <div className="pb-400 flex-col flex-fill overflow-auto gap-400">
        {DEFAULT_STYLE_MODELS_PROPERTIES[model.uuid]?.description && (
          <div className="p-400 bg-input-surface-rest rounded-100 body-md-default">
            {DEFAULT_STYLE_MODELS_PROPERTIES[model.uuid]?.description}
          </div>
        )}
        <div className="flex-col gap-200">
          <span className="items-center label-md-semibold">Initial images</span>
          <TrainingImagesGrid model={modelWithContents} />
        </div>
        <div className="flex-col gap-200">
          <div className="items-center label-md-semibold">Attributes</div>
          <ModelDetails modelUuid={model.uuid} />
        </div>
      </div>
    </div>
  );
};

const TrainingImagesGrid = ({ model }: { model: ModelWithContents }) => {
  const [isShowingMore, setIsShowingMore] = useState(false);

  const trainingImagesUrls =
    DEFAULT_STYLE_MODELS_PROPERTIES[model.uuid]?.image_training_urls ??
    model.contents.map((image) => image.url);

  return (
    <div className="flex-col-center gap-200">
      <Masonry columnsCount={4} gutter="4px">
        {trainingImagesUrls
          .slice(0, isShowingMore ? undefined : 4)
          .map((url) => (
            <BaseImage
              key={url}
              src={url}
              className={classNames(
                isShowingMore ? "" : "h-full w-full aspect-square",
              )}
              imageClassName={classNames(
                isShowingMore ? "" : "h-full w-full object-cover object-center",
              )}
            />
          ))}
      </Masonry>
      {model.contents.length > 4 && (
        <Button
          variant="tertiary"
          size="sm"
          className="w-[80px]"
          onClick={() => setIsShowingMore((previousValue) => !previousValue)}
        >
          Show {isShowingMore ? "less" : "more"}
        </Button>
      )}
    </div>
  );
};
